import { useEffect, useRef } from "react";

interface GuardEntry {
  selector: string;
  condition?: () => boolean;
}

interface UseNavigationGuardParams {
  isActive: boolean;
  onTrigger: () => void;
  skipSelector?: string;
  suppressSelector?: string;
  guardEntries?: GuardEntry[];
  isSuppressed?: boolean;
}

export function useNavigationGuard({ isActive, onTrigger, skipSelector, suppressSelector, guardEntries, isSuppressed }: UseNavigationGuardParams) {
  const isActiveRef = useRef(isActive);
  const onTriggerRef = useRef(onTrigger);
  const skipSelectorRef = useRef(skipSelector);
  const suppressSelectorRef = useRef(suppressSelector);
  const guardEntriesRef = useRef(guardEntries);
  const isSuppressedRef = useRef(isSuppressed ?? false);
  const isLeavingRef = useRef(false);
  const isBypassingRef = useRef(false);
  const pendingHrefRef = useRef<string | null>(null);

  useEffect(() => {
    isActiveRef.current = isActive;
    onTriggerRef.current = onTrigger;
    skipSelectorRef.current = skipSelector;
    suppressSelectorRef.current = suppressSelector;
    guardEntriesRef.current = guardEntries;
    isSuppressedRef.current = isSuppressed ?? false;
  });

  useEffect(() => {
    // The sentinel entries pushed below were never scrolled on, so the
    // browser's default scroll restoration jumps the page to the top the
    // instant Back is pressed — before this handler even runs. Take over
    // restoration ourselves so nothing auto-scrolls.
    const previousScrollRestoration = window.history.scrollRestoration;
    window.history.scrollRestoration = "manual";

    const pushSentinel = () => window.history.pushState({ reservationGuard: true }, "", window.location.pathname);

    // Safari skips history entries pushed without a user gesture when pressing Back,
    // and (unlike Chrome) doesn't un-skip them after later interaction. So besides the
    // mount-time push, push a gesture-backed sentinel on the next user interaction.
    let needsGesturePush = true;
    pushSentinel();

    const handleUserGesture = () => {
      if (!needsGesturePush || !isActiveRef.current || isLeavingRef.current || isSuppressedRef.current) return;
      // Scroll touches don't grant activation; wait for a real tap so the push isn't wasted.
      const userActivation = (navigator as Navigator & { userActivation?: { isActive: boolean } }).userActivation;
      if (userActivation && !userActivation.isActive) return;
      needsGesturePush = false;
      pushSentinel();
    };

    const handlePopState = () => {
      if (isActiveRef.current && !isSuppressedRef.current) {
        pendingHrefRef.current = null;
        // This push has no user gesture, so re-arm for one. Trade-off: each Back → Stay
        // cycle leaves one extra sentinel in history, so after leaving, Back from the
        // next page steps through more reservation entries.
        pushSentinel();
        needsGesturePush = true;
        onTriggerRef.current();
      }
    };

    // Safari bfcache: page restored from cache won't re-run useEffect,
    // so re-push the sentinel state to keep back navigation intercepted.
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        pushSentinel();
        needsGesturePush = true;
      }
    };

    // Intercepts elements that navigate programmatically (e.g. window.location.href)
    // where a plain anchor check won't work. Each entry's condition gates the intercept
    // so the same element can behave differently on desktop vs mobile.
    const handleGuardClick = (event: MouseEvent) => {
      if (!isActiveRef.current || isLeavingRef.current || isSuppressedRef.current) return;
      const entries = guardEntriesRef.current;
      if (!entries?.length) return;
      const clicked = event.target as Element;
      const match = entries.find(({ selector }) => clicked.closest(selector));
      if (!match) return;
      if (match.condition && !match.condition()) return;
      const anchor = clicked.closest<HTMLAnchorElement>("a[href]");
      pendingHrefRef.current = anchor ? anchor.href : null;
      event.preventDefault();
      event.stopImmediatePropagation();
      onTriggerRef.current();
    };

    // Intercepts navbar/logo link clicks so the custom modal shows for those too.
    // skipSelector and suppressSelector both bypass the modal and mark as bypassing
    // so the native beforeunload dialog is also suppressed for those navigations.
    const handleLinkClick = (event: MouseEvent) => {
      if (!isActiveRef.current || isLeavingRef.current || isSuppressedRef.current) return;
      const clicked = event.target as Element;
      if (skipSelectorRef.current && clicked.closest(skipSelectorRef.current)) {
        isBypassingRef.current = true;
        setTimeout(() => { isBypassingRef.current = false; }, 0);
        return;
      }
      if (suppressSelectorRef.current && clicked.closest(suppressSelectorRef.current)) {
        isBypassingRef.current = true;
        setTimeout(() => { isBypassingRef.current = false; }, 0);
        return;
      }
      const anchor = clicked.closest<HTMLAnchorElement>("a[href]");
      if (!anchor) return;
      const { href, target } = anchor;
      if (href.startsWith("javascript:") || target === "_blank") return;
      const url = new URL(href);
      if (url.origin === window.location.origin && url.pathname === window.location.pathname) return;
      pendingHrefRef.current = href;
      event.preventDefault();
      event.stopImmediatePropagation();
      onTriggerRef.current();
    };

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!isActiveRef.current || isLeavingRef.current || isSuppressedRef.current || isBypassingRef.current) return;
      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("popstate", handlePopState);
    window.addEventListener("pageshow", handlePageShow);
    window.addEventListener("beforeunload", handleBeforeUnload);
    // pointerdown only counts as user activation for mouse; touch (iOS) activates on pointerup/touchend.
    document.addEventListener("pointerdown", handleUserGesture, true);
    document.addEventListener("pointerup", handleUserGesture, true);
    document.addEventListener("touchend", handleUserGesture, true);
    document.addEventListener("keydown", handleUserGesture, true);
    document.addEventListener("click", handleGuardClick, true);
    document.addEventListener("click", handleLinkClick, true);

    return () => {
      window.history.scrollRestoration = previousScrollRestoration;
      window.removeEventListener("popstate", handlePopState);
      window.removeEventListener("pageshow", handlePageShow);
      window.removeEventListener("beforeunload", handleBeforeUnload);
      document.removeEventListener("pointerdown", handleUserGesture, true);
      document.removeEventListener("pointerup", handleUserGesture, true);
      document.removeEventListener("touchend", handleUserGesture, true);
      document.removeEventListener("keydown", handleUserGesture, true);
      document.removeEventListener("click", handleGuardClick, true);
      document.removeEventListener("click", handleLinkClick, true);
    };
  }, []);

  const markAsLeaving = () => {
    isLeavingRef.current = true;
  };

  return { markAsLeaving, pendingHrefRef };
}
